import { Buffer } from 'buffer';
import { v4 as uuid } from 'uuid';
import {
  DocumentItem,
  AccountItem,
} from '@pango-inc/vault.wasm/dist/vault/vault.types';
import * as lodash from 'lodash';
import { AppDispatch, AppState } from 'app/store';
import { logErrors } from 'utils';
import { GENERAL } from 'utils/constants';
import getParsedURL from 'utils/getParsedURL';
import {
  getIsAccessTokenValid,
  selectUserSignInStatus,
} from 'services/session/sessionSelectors';
import { refreshApToken } from 'services/session/sessionAsync';
import {
  DocumentWithFiles,
  FileItem,
} from '@pango-inc/keybox-sdk-wrapper/dist/sdk-wrapper.main';
import * as SDK from './sdkFunctions';
import {
  setAppInfo,
  setIsLoading,
  setVaultAccounts,
  setVaultDocuments,
  setVaultExists,
} from './vaultSlice';
import { downloadFile } from './utils';

let isInProgress = false;
let inProgressPromise: Promise<void> | null = null;
/*
Refresh Token and Access Tokens
*/

export function doValidateTokenAndSetNewIfExpired(ignoreInProgress = false) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    const userSignInStatus = selectUserSignInStatus(getState());
    if (!userSignInStatus) {
      return null;
    }
    if (!ignoreInProgress && isInProgress) {
      return inProgressPromise;
    }

    isInProgress = true;
    inProgressPromise = (async () => {
      try {
        const state = getState();
        const accessToken = getIsAccessTokenValid(state);

        if (!accessToken) {
          try {
            const response = await dispatch(refreshApToken());
            if (response) {
              await dispatch(SDK.executeSetTokens());
            }
          } catch (error) {
            await dispatch(SDK.executeLockVault());
          }
        } else {
          await dispatch(SDK.executeSetTokens());
        }
      } catch (error) {
        logErrors(error);
      } finally {
        isInProgress = false;
        inProgressPromise = null;
      }
    })();

    return inProgressPromise;
  };
}

// Passwords

/**
 * Adds an account to the vault.
 *
 * @param account - The account item to be added to the vault.
 * @returns A promise that resolves to the addition result if the addition is successful, or a rejected promise if the addition fails.
 */
export function doAddAccount(account: AccountItem[]) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const addAccountResult = await dispatch(
        SDK.executeSaveAccountItems(account),
      );
      if (addAccountResult) {
        return addAccountResult;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doUpdateAccount(account: AccountItem) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const updateAccountResult = await dispatch(
        SDK.executeUpdateAccountItem(account),
      );
      if (updateAccountResult) {
        return updateAccountResult;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// TODO: Phase 2 implementation
export function doSearchVaultAccounts(searchTerm: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const searchResult = await dispatch(
        SDK.executeSearchVaultAccounts(searchTerm),
      );
      return searchResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// TODO: Phase 2 implementation
export function doGetAccountItemLimits() {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const limits = await dispatch(SDK.executeGetCurrentAccountItemLimits());
      return limits;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Retrieves all account items from the vault.
 *
 * @param dispatch - The dispatch function to dispatch actions to the Redux store.
 * @returns A promise that resolves to the retrieval result if the retrieval is successful, or a rejected promise if the retrieval fails.
 */
export function doGetPasswordAccounts() {
  // eslint-disable-next-line consistent-return
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const accounts = await dispatch(SDK.executeGetAccountItems());
      if (lodash.isArray(accounts)) {
        dispatch(setVaultAccounts(accounts));
      }
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doDeleteAccount(accountId: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const deleteResult = await dispatch(
        SDK.executeDeleteAccountItem(accountId),
      );
      if (deleteResult) {
        return deleteResult;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doRevealPassword(accountID: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const revealResult = await dispatch(SDK.executeRevealPassword(accountID));
      if (revealResult) {
        return revealResult;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doEvaluatePasswordStrength(password: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const strength = await dispatch(
        SDK.executeEvaluatePasswordStrength(password),
      );
      if (strength) {
        return strength;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doImportAccountsFromFile(content: string, fileName: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const importResult = await dispatch(
        SDK.executeImportPasswordsFromFile(content, fileName),
      );
      const accounts = importResult.payload.accountItems
        .map(({ id, uri: unNormalizeUrl, ...rest }) => {
          // eslint-disable-next-line no-param-reassign
          if (!id) id = uuid();
          const { title, domain } = getParsedURL(unNormalizeUrl);
          return { ...rest, title, uri: unNormalizeUrl, id, domain };
        })
        .filter((account) => Boolean(account.uri));

      await dispatch(SDK.executeSaveAccountItems(accounts));
      return accounts;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doExportAccountsToFile() {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const { payload } = await dispatch(SDK.executeExportAccountsFromVault());

      const decodedString = Buffer.from(
        payload.accountsBase64,
        'base64',
      ).toString('utf-8');
      const blob = new Blob([decodedString], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.setAttribute('href', url);
      link.setAttribute('download', 'accounts.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doGetDigitalDocuments() {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const files = await dispatch(SDK.executeGetDocuments());
      if (lodash.isArray(files)) dispatch(setVaultDocuments(files));
      else dispatch(setVaultDocuments([]));
      return files;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/*
 Digital file related functions
 Mainly deals with file upload, download, delete, etc.

 */
export function doAddDocument(data: DocumentItem) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const result = await dispatch(SDK.executeAddDocuments(data));
      // await dispatch(doGetDigitalDocuments());
      if (result && result[0]) {
        return result[0];
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doUpdateDocument(data: DocumentWithFiles) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const result = await dispatch(SDK.executeUpdateDocuments(data));
      // await dispatch(doGetDigitalDocuments());
      if (result && result[0]) {
        return result[0];
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doUploadFile(documentId: string, data: Array<[FileItem]>) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const uploadResult = await dispatch(
        SDK.executeUploadFiles(documentId, data),
      );
      if (uploadResult) {
        // await dispatch(doGetDigitalDocuments());
        return uploadResult;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doDeleteDocument(documentId: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const deleteResult = await dispatch(
        SDK.executeDeleteDocumentsById(documentId),
      );
      if (deleteResult) {
        await dispatch(doGetDigitalDocuments());
        return deleteResult;
      }
      throw new Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doDeleteFileAttachment(fileId: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const deleteResult = await dispatch(SDK.executeDeleteFiles(fileId));
      if (deleteResult) {
        await dispatch(doGetDigitalDocuments());
        return deleteResult;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doDownloadDocumentAttachments(documentId: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const files = await dispatch(SDK.executeDownloadAllFiles(documentId));
      if (files && files.length > 0) {
        files.forEach((file, index) => {
          downloadFile(file, index * 250);
        });
        return files;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doDownloadFileAttachment(fileId: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const files = await dispatch(SDK.executeDownloadFiles(fileId));
      if (files && files.length > 0) {
        files.forEach((file, index) => {
          downloadFile(file, index * 250);
        });
        return files;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doPreviewFileAttachment(fileId: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const files = await dispatch(SDK.executeDownloadFiles(fileId));
      if (files && files.length > 0) {
        return files[0];
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Logs the user into the vault using the stored tokens.
 *
 * This function logs the user into the vault by attempting to set the tokens using the stored access token and refresh token.
 * If the login is successful, it updates the application's state with the login result and returns the login result.
 * If the login fails, it updates the application's state with the login failure and returns a rejected promise.
 *
 * @param dispatch - The dispatch function to dispatch actions to the Redux store.
 * @returns A promise that resolves to the login result if the vault exists, or a rejected promise if the login fails.
 */
export function doLoginVault() {
  return async (dispatch: AppDispatch) => {
    try {
      const loginResult = await dispatch(SDK.executeSetTokens());
      const AppInfo = {
        isAuthorized: true,
        isLocked: true,
      };
      dispatch(setAppInfo(AppInfo));
      return loginResult;
    } catch (error) {
      const AppInfo = {
        isAuthorized: false,
        isLocked: true,
      };
      dispatch(setAppInfo(AppInfo));
      return Promise.reject(error);
    }
  };
}

// TODO: need to update this function once wrapper is ready
/**
 * Checks if the vault exists.
 *
 * This function checks if the vault exists by attempting to login using the stored tokens.
 * If the login is successful, it returns the login result. If the login fails, it returns a rejected promise.
 *
 * @param dispatch - The dispatch function to dispatch actions to the Redux store.
 * @returns A promise that resolves to the login result if the vault exists, or a rejected promise if the login fails.
 */
export function doCheckIfVaultExists() {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired(true));
      await dispatch(doLoginVault());
      const result = await dispatch(SDK.executeVaultExists());
      await dispatch(setVaultExists(result.isVaultExist));
      await dispatch(setIsLoading(false));
      return result.isVaultExist;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doLockVault() {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      await dispatch(SDK.executeLockVault());
      await dispatch(SDK.executeExitVault());
      const AppInfo = {
        isAuthorized: false,
        isLocked: true,
      };
      dispatch(setAppInfo(AppInfo));
      return true;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Unlocks the vault using the provided master password.
 *
 * @param MasterPassword - The master password to unlock the vault.
 * @returns The result of the unlock operation.
 */
export function doUnlockVault(MasterPassword: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      await dispatch(doLockVault());
      const initResult = await dispatch(SDK.executeUnlockVault(MasterPassword));
      await dispatch(doGetPasswordAccounts());
      await dispatch(doGetDigitalDocuments());
      // TODO: Get Documents
      const AppInfo = {
        isAuthorized: true,
        isLocked: false,
      };
      dispatch(setAppInfo(AppInfo));
      return initResult;
    } catch (error) {
      const AppInfo = {
        isAuthorized: true,
        isLocked: true,
      };
      dispatch(setAppInfo(AppInfo));
      return Promise.reject(error);
    }
  };
}

/**
 * Creates a new vault with the provided master password and recovery key.
 *
 * @param masterPassword - The master password to secure the vault.
 * @returns The recovery key generated for the vault.
 */
export function doCreateMasterPassword(
  masterPassword: string,
  recoveryKey: string,
) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      await dispatch(SDK.executeCreateVault(masterPassword, recoveryKey));
      const result = await dispatch(SDK.executeVaultExists());
      if (result) {
        dispatch(setVaultExists(result.isVaultExist));
        return recoveryKey;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doGenerateRecoveryKey() {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const recovery = await dispatch(SDK.executeGenerateRecoveryKey());
      if (recovery) {
        return recovery.payload;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function doValidateRecoveryKey(recoveryKey: string) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doLockVault());
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const result = await dispatch(
        SDK.executeUnlockUsingRecoveryKey(recoveryKey),
      );
      if (result) {
        return lodash.isEmpty(result);
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Changes the master password of the vault.
 *
 * @param newPassword - The new master password to secure the vault.
 * @param recoveryKey - The recovery key generated for the new vault.
 * @returns The recovery key generated for the new vault.
 */
export function doChangeMasterPassword(
  newPassword: string,
  recoveryKey: string,
) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(doValidateTokenAndSetNewIfExpired());
      const changePasswordResult = await dispatch(
        SDK.executeChangeMasterPassword(newPassword, recoveryKey),
      );
      if (changePasswordResult) {
        return changePasswordResult;
      }
      throw Error(GENERAL.defaultErrorMsg);
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
